import React, { useState, useMemo, useEffect, useCallback } from "react";
import CheckBoxList from "components/controls/ListBox/CheckBoxList";
import { handleKeyDownEvent } from "utils/utilsLogic";
import { ButtonQuarterlyData, MonthData } from "types/PeriodSelectionTypes";
import * as Enums from "enums/enums";
import Const from "const.json";
import { CiCircleChevLeft, CiCircleChevRight } from "react-icons/ci";
import "./BasePeriodSelector.scss";

type PeriodSelectorProps = {
  periods: number[] | MonthData[] | ButtonQuarterlyData[];
  renderButtonContent: (period: any) => JSX.Element;
  selectionLabel: string;
  maxSelection?: number;
  defaultPeriodIndex?: number;
  onSelectionChange: (
    selectedValues: string,
    selectedKeys: any,
    selectedIndexes: number[]
  ) => void;
  indexes: number[];
  selectionMode: "single" | "multiple";
};

const BasePeriodSelector: React.FC<PeriodSelectorProps> = (PeriodProps) => {
  const [startIndex, setStartIndex] = useState(0);
  const endIndex = useMemo(
    () => startIndex + Const.PERIOD_MAX_SELECTION,
    [startIndex]
  );
  const shouldDisplayArrows =
    PeriodProps.periods.length > Const.PERIOD_MAX_SELECTION;

  const initialPeriods = () => {
    if (PeriodProps.defaultPeriodIndex && !PeriodProps.indexes) {
      const index = PeriodProps.periods.findIndex(
        (period) => period === PeriodProps.defaultPeriodIndex
      );
      return index !== -1 ? [index] : [];
    }
    return PeriodProps.indexes ?? [];
  };

  const [selectedPeriods, setSelectedPeriods] =
    useState<number[]>(initialPeriods);

  const getSelectedValuesStringAndKeys = useCallback(() => {
    const sortedSelectedPeriods = [...selectedPeriods].sort((a, b) => a - b);
    const selectedValuesAndKeys = sortedSelectedPeriods.map((periodIndex) => {
      const period = PeriodProps.periods[periodIndex];
      return typeof period === "object"
        ? { value: period.description, key: period.months }
        : { value: period, key: period };
    });

    return selectedValuesAndKeys;
  }, [selectedPeriods, PeriodProps.periods]);
  useEffect(() => {
    const selectedValuesAndKeys = getSelectedValuesStringAndKeys();
    const selectedValuesString = selectedValuesAndKeys
      .map((item) => item.value)
      .join(", ");
    const selectedKeys = selectedValuesAndKeys.map((item) => item.key);
    PeriodProps.onSelectionChange(
      selectedValuesString,
      selectedKeys,
      selectedPeriods
    );
  }, [selectedPeriods]);

  const handleArrowClick = (direction: Enums.NextOrPrevEnum) => {
    let newIndex = startIndex + direction;

    if (newIndex >= PeriodProps.periods.length) {
      newIndex = 0;
    } else if (newIndex < 0) {
      newIndex = PeriodProps.periods.length - 1;
    }
    setStartIndex(newIndex);
  };

  const getDisplayedPeriods = (): Array<any> =>
    PeriodProps.periods.slice(startIndex, endIndex);

  return (
    <div className="custom-period-selector">
      <p className="custom-period-selector-label">
        {PeriodProps.selectionLabel}
      </p>
      <div className="d-flex w-100 align-items-center justify-content-center">
        {shouldDisplayArrows && (
          <CiCircleChevRight
            className={`${startIndex === 0 ? "disabled" : ""
              } custom-period-nav-button`}
            tabIndex={0}
            onClick={() => handleArrowClick(Enums.NextOrPrevEnum.Prev)}
            onKeyDown={(event) =>
              handleKeyDownEvent(event, () =>
                handleArrowClick(Enums.NextOrPrevEnum.Prev)
              )
            }
          />
        )}
        <CheckBoxList selectionMode={PeriodProps.selectionMode} setSelectedItem={setSelectedPeriods} lastProps={PeriodProps} items={getDisplayedPeriods()} selectedItems={selectedPeriods} startIndex={startIndex} />
        {shouldDisplayArrows && (
          <CiCircleChevLeft
            onClick={() => handleArrowClick(Enums.NextOrPrevEnum.Next)}
            className={`${endIndex === PeriodProps.periods.length ? "disabled" : ""
              } custom-period-nav-button`}
            tabIndex={0}
            onKeyDown={(event) =>
              handleKeyDownEvent(event, () =>
                handleArrowClick(Enums.NextOrPrevEnum.Next)
              )
            }
          />
        )}
      </div>
    </div>
  );
};

export default BasePeriodSelector;